import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useApi = (apiFunc, config) => {
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef(null);

  const triggerFetchDefaultOption = useMemo(
    () => ({
      returnOnly: false,
      args: config?.args,
      enabled: true,
      signal: null,
    }),
    [config]
  );

  const triggerFetch = useCallback(
    async (option = triggerFetchDefaultOption) => {
      const options = { ...triggerFetchDefaultOption, ...option };
      if (options.enabled === false) return;

      if (!options.signal) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        options.signal = controller.signal;
      }

      if (!options.skipLoadingState) {
        setIsLoading(true);
      }
      let response;
      try {
        const args = options && options.args;
        if (Array.isArray(args)) {
          response = await apiFunc(...args, options.signal);
        } else {
          response = await apiFunc(args, options.signal);
        }
        if (options.returnOnly) {
          return response;
        }
        if (response?.status) {
          config?.onSuccess?.(response);
        } else {
          config?.onError?.(response);
        }
      } catch (error) {
        const errorName = error && error.name;
        if (errorName !== 'AbortError' && errorName !== 'CanceledError') {
          config?.onError?.(error);
        }
      } finally {
        if (!options.skipLoadingState) {
          setIsLoading(false);
        }
      }
      return response;
    },
    [apiFunc, config, triggerFetchDefaultOption]
  );

  useEffect(() => {
    if (!(config?.type === 'mutation' || config?.enabled === false)) {
      triggerFetch();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    triggerFetch,
    isLoading,
    setIsLoading,
  };
};
